/**
 * BuscadorGlobal.tsx — Campo de búsqueda global del espacio de trabajo (T3 · §6).
 *
 * Primitiva presentacional del buscador global (criterios shell §6): campo de
 * texto con icono de lupa, contador de coincidencias («N coincidencias» /
 * «✓ nombre» / «sin resultados») y botón «×» que vacía SOLO el texto — nunca
 * toca la selección (el consumidor es dueño de ella; §6: el elemento cargado
 * es el foco de trabajo, independiente de cómo se llegó a él).
 *
 * Sin lógica de negocio: no busca ni selecciona; recibe el estado por props y
 * emite callbacks. La semántica «localiza y selecciona» (no filtra) la cablea
 * el consumidor (P3 hoy; P2 en P2-S2). Reutilizable en cualquier paso.
 *
 * @date 2026-07-03 · sesión T3BG
 */

import type { ChangeEvent, CSSProperties, FC, KeyboardEvent } from 'react';
import { Search, X } from 'lucide-react';
import { colors, typography, borderRadius } from '@/design-system/v2';

/**
 * Normaliza un texto para búsqueda: descompone acentos (NFD), elimina
 * diacríticos, pasa a minúsculas y recorta espacios. Así «huetor» encuentra
 * «Huétor» y viceversa (útil también con corrupción UTF-8 parcial).
 */
export function normalizarBusqueda(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

export interface BuscadorGlobalProps {
  /** Texto de búsqueda (controlado). */
  valor: string;
  /** Cambio del texto; «×» emite onCambio(''). Nunca toca la selección. */
  onCambio: (texto: string) => void;
  /** Número de coincidencias del texto actual (lo calcula el consumidor). */
  nCoincidencias: number;
  /**
   * Nombre del elemento seleccionado SI pertenece a las coincidencias
   * actuales; con él el contador muestra «✓ nombre». Def: null.
   */
  nombreSeleccionado?: string | null;
  /** Intro sobre el campo: seleccionar la primera coincidencia. */
  onSeleccionarPrimera?: () => void;
  /** Texto de ayuda del campo. Def: «Buscar por nombre o tipología…». */
  placeholder?: string;
}

const S: Record<string, CSSProperties> = {
  cont: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  campo: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '4px 8px',
    border: `1px solid ${colors.border.DEFAULT}`,
    borderRadius: borderRadius.full,
    background: colors.bg.secondary,
  },
  input: {
    border: 'none',
    outline: 'none',
    background: 'transparent',
    color: colors.fg.primary,
    fontFamily: typography.fontFamily.sans,
    fontSize: typography.fontSize.sm,
    width: '180px',
  },
  limpiar: {
    display: 'inline-flex',
    alignItems: 'center',
    border: 'none',
    background: 'transparent',
    color: colors.fg.muted,
    cursor: 'pointer',
    padding: 0,
  },
  contador: {
    fontFamily: typography.fontFamily.mono,
    fontSize: typography.fontSize.xs,
    color: colors.fg.muted,
    whiteSpace: 'nowrap',
  },
  contadorOk: {
    color: colors.success,
  },
};

/** Contador según §6: «✓ nombre» / «N coincidencias» / «sin resultados». */
const textoContador = (
  valor: string,
  n: number,
  nombre: string | null,
): { texto: string; ok: boolean } | null => {
  if (valor.trim() === '') return null;
  if (nombre != null) return { texto: `✓ ${nombre}`, ok: true };
  if (n === 0) return { texto: 'sin resultados', ok: false };
  return { texto: n === 1 ? '1 coincidencia' : `${n} coincidencias`, ok: false };
};

export const BuscadorGlobal: FC<BuscadorGlobalProps> = ({
  valor,
  onCambio,
  nCoincidencias,
  nombreSeleccionado = null,
  onSeleccionarPrimera,
  placeholder = 'Buscar por nombre o tipología…',
}) => {
  const contador = textoContador(valor, nCoincidencias, nombreSeleccionado);

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') onSeleccionarPrimera?.();
  };

  return (
    <div data-testid="buscador-global" style={S.cont}>
      <span style={S.campo}>
        <Search size={13} color={colors.fg.muted} aria-hidden="true" />
        <input
          data-testid="buscador-global-input"
          type="text"
          value={valor}
          placeholder={placeholder}
          aria-label="Buscar infraestructura"
          style={S.input}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onCambio(e.target.value)}
          onKeyDown={onKeyDown}
        />
        {valor !== '' && (
          <button
            type="button"
            data-testid="buscador-global-limpiar"
            aria-label="Vaciar búsqueda"
            title="Vaciar búsqueda (conserva la selección)"
            style={S.limpiar}
            onClick={() => onCambio('')}
          >
            <X size={13} aria-hidden="true" />
          </button>
        )}
      </span>
      {contador != null && (
        <span
          data-testid="buscador-global-contador"
          style={{ ...S.contador, ...(contador.ok ? S.contadorOk : undefined) }}
        >
          {contador.texto}
        </span>
      )}
    </div>
  );
};
